import React, { useEffect, useRef, useState } from "react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import ArrowLeftIcon from "./Icons/ArrowLeft";
import ArrowRightIcon from "./Icons/ArrowRight";
import SafeSiteImage from "./SafeSiteImage";
import { graphql, useStaticQuery } from "gatsby";

const AccreditationsStyles = styled(Swiper)`
  ${({ ...props }) => css`
    width: calc(100% - 128px);

    @media (min-width: ${props.theme.breakpoints.sm}) {
      width: calc(100% - 144px);
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      width: calc(100% - 192px);
    }

    .swiper-button-prev,
    .swiper-button-next {
      display: none;
    }

    .swiper-slide {
      height: auto;

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      > a {
        height: 100%;
        display: flex;
      }
    }
  `}
`;

const AccreditationsWrapperStyles = styled.div`
  ${({ ...props }) => css`
    .container {
      position: relative;
    }
  `}
`;

const NavigationStyles = styled.div`
  ${({ ...props }) => css`
    display: ${(props.$hidden ? "none" : "flex")};
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  `}
`;

const StyledButton = styled.button`
  ${({ ...props }) => css`
    background: #e8e8e8;
    line-height: 32px;
    padding: 0;
    border-radius: 50%;
    transition: all ease 0.3s;
    height: 32px;
    width: 32px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 24px;
    z-index: 9;

    &.next {
      right: 16px;
    }

    &.prev {
      left: 16px;
    }

    @media (min-width: ${props.theme.breakpoints.sm}) {
      &.next {
        right: 24px;
      }

      &.prev {
        left: 24px;
      }
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      &.next {
        right: 48px;
      }

      &.prev {
        left: 48px;
      }
    }

    &:hover {
      background: #dedede;
    }

    &.swiper-button-disabled {
      opacity: 0.5;
      cursor: default;

      &:hover {
        background: none;
      }
    }
  `}
`;

SwiperCore.use([Navigation, Pagination, Autoplay]);

export const Accreditations = ({
  global = false,
  images,
  autoScroll = true,
  delay = 3000
}) => {
  if (global && !process.env.STORYBOOK) {
    const { wp } = useStaticQuery(graphql`
      query GlobalAccreditationsQuery {
        wp {
          siteSettings {
            siteSettings {
              accreditationsImages {
                image {
                  altText
                  mediaItemUrl
                }
                link {
                  url
                  target
                  title
                }
              }
              accreditationsAutoScroll
              accreditationsDelay
            }
          }
        }
      }
    `);
    images = wp.siteSettings.siteSettings.accreditationsImages;
    autoScroll = wp.siteSettings.siteSettings.accreditationsAutoScroll;
    delay = wp.siteSettings.siteSettings.accreditationsDelay;
  }

  if (!Array.isArray(images)) {
    return <></>;
  }

  images = images.filter(image => image.image?.mediaItemUrl);

  if (images.length === 0) {
    return <></>;
  }

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const [autoplay, setAutoplay] = useState(autoScroll);
  const [slidesPerView, setSlidesPerView] = useState(5);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.params.navigation.prevEl = prevRef.current;
      swiperRef.current.params.navigation.nextEl = nextRef.current;
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
    }
  }, [swiperRef.current, prevRef.current, nextRef.current]);

  useEffect(() => {
    const updateSlidesPerView = () => {
      if (window.innerWidth < 577) {
        setSlidesPerView(2);
      } else if (window.innerWidth < 992) {
        setSlidesPerView(3);
      } else {
        setSlidesPerView(5);
      }
    };
  
    updateSlidesPerView();
  
    window.addEventListener("resize", updateSlidesPerView);
  
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, []);

  return (
    <AccreditationsWrapperStyles>
      <Section>
        <Container className="container">
          <AccreditationsStyles
            ref={swiperRef}
            spaceBetween={64}
            slidesPerView={slidesPerView}
            wrapperTag="ul"
            navigation
            loop
            autoplay={autoplay ? { delay, disableOnInteraction: true } : false}
            speed={500}
            onSlideChange={() => setAutoplay(false)}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
          >
            {images.map((image, index) => (
              <SwiperSlide key={image.image.mediaItemUrl + index} tag="li">
                <a href={image.link.url} target={image.link.target} title={image.link.title}>
                  <SafeSiteImage
                    image={image.image.mediaItemUrl}
                    alt={image.image.altText}
                  />
                </a>
              </SwiperSlide>
            ))}
          </AccreditationsStyles>
          <NavigationStyles
            $hidden={images.length === 1}
          >
            <StyledButton ref={prevRef} type="button" className="prev">
              <span className="sr-only">Prev</span>
              <ArrowLeftIcon />
            </StyledButton>
            <StyledButton ref={nextRef} type="button" className="next">
              <span className="sr-only">Next</span>
              <ArrowRightIcon />
            </StyledButton>
          </NavigationStyles>
        </Container>
      </Section>
    </AccreditationsWrapperStyles>
  )
};
